import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';
import { tokenStorage, getErrorMessage } from '../utils/helpers';

// Initial auth state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Auth actions
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER',
};

// Auth reducer
const authReducer = (state, action) => {
  console.log('AuthReducer - Action:', action.type, 'Payload:', action.payload);
  
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.SET_ERROR:
      console.log('AuthReducer - Setting error to:', action.payload);
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      console.log('AuthReducer - Clearing error');
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    default:
      return state;
  }
};

// Create contexts
const AuthContext = createContext();
const AuthDispatchContext = createContext();

// Auth provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const initializeAuth = useCallback(async () => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

    try {
      const accessToken = tokenStorage.getAccessToken();
      
      if (!accessToken || tokenStorage.isTokenExpired(accessToken)) {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        return;
      }

      // Verify token with server and get current user
      const response = await authApi.getCurrentUser();
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user: response.user },
      });
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      // If token is invalid, clear it and logout
      tokenStorage.clearTokens();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  }, []);

  // Initialize auth state on mount - only run once
  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    // Don't clear error at start - let it persist until we get a result

    try {
      const response = await authApi.login(credentials);
      
      // Store tokens
      tokenStorage.setTokens(
        response.tokens.accessToken,
        response.tokens.refreshToken
      );

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user: response.user },
      });

      return { success: true, user: response.user };
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = getErrorMessage(error);
      console.log('Error message being set:', errorMessage);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

    try {
      const response = await authApi.register(userData);
      
      // Store tokens
      tokenStorage.setTokens(
        response.tokens.accessToken,
        response.tokens.refreshToken
      );

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user: response.user },
      });

      return { success: true, user: response.user };
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      tokenStorage.clearTokens();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  const updateUser = (userData) => {
    dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: userData });
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const refreshUser = async () => {
    try {
      const response = await authApi.getCurrentUser();
      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: response.user,
      });
      return response.user;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // If refresh fails, logout
      logout();
      throw error;
    }
  };

  const authActions = {
    login,
    register,
    logout,
    updateUser,
    clearError,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={state}>
      <AuthDispatchContext.Provider value={authActions}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthContext.Provider>
  );
}

// Custom hooks
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useAuthActions() {
  const context = useContext(AuthDispatchContext);
  if (context === undefined) {
    throw new Error('useAuthActions must be used within an AuthProvider');
  }
  return context;
}

// Combined hook for convenience
export function useAuthComplete() {
  const auth = useAuth();
  const authActions = useAuthActions();
  
  return {
    ...auth,
    ...authActions,
  };
}